<script setup lang="ts">
import type { ValuePropositionSectionProps } from './types';
import type Carousel from '@/components/ui/carousel/Carousel.vue';

import { cn } from '@/utils';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import { PAGINATION_DELAY_MS, valuePropositionItems } from './content';

const props = withDefaults(defineProps<ValuePropositionSectionProps>(), {
	items: () => valuePropositionItems,
	isLoading: false
});

// Refs for carousel control
const carouselContainerRef = ref<InstanceType<typeof Carousel> | null>(null);
const carouselItemSelectorContainerRef = ref<HTMLDivElement | null>(null);

const {
	currentCarouselItemIndex,
	scrollableSlidesNumber,
	carouselDynamicItemWidth,
	scrollTo
} = useCarouselControl({
	carouselContainerRef,
	carouselItemSelectorContainerRef
});
</script>

<template>
	<section
		class="relative w-full overflow-x-hidden bg-gradient-to-r from-[#1a1a1a] to-black"
	>
		<!-- Background decorative elements -->
		<div class="absolute inset-0 overflow-hidden">
			<!-- Geometric form from Figma - Desktop -->

			<svg
				class="absolute left-3 hidden h-[994.872px] w-[1570px] md:left-4 md:top-[-23rem] md:block lg:left-7 lg:top-[-400px] xl:left-6 2xl:left-[60px] 2xl:top-[-350px] 3xl:left-96 3xl:top-[-650px] 3xl:h-[1373px] 3xl:w-full"
				xmlns="http://www.w3.org/2000/svg"
				width="1476"
				height="337"
				viewBox="0 0 1476 337"
				fill="none"
			>
				<path
					opacity="0.2"
					d="M100.801 336.875L0.91623 268.104L0 139.06L1046.52 -644.769L1117.81 -657.997L1248.4 -480.231L1570 -11.5962L1532.15 102.288L100.801 336.875Z"
					fill="#4C4C4C"
				/>
			</svg>

			<!-- Geometric form from Figma - Mobile -->
			<svg
				class="absolute -top-20 left-3 h-[363.637px] w-[503.616px] md:hidden"
				xmlns="http://www.w3.org/2000/svg"
				width="363"
				height="287"
				viewBox="0 0 363 287"
				fill="none"
			>
				<path
					opacity="0.2"
					d="M66.4486 286.641L0.603984 241.306L0 156.24L340.364 -68.276L387.36 -76.9965L473.446 40.1877L503.616 163.745L478.664 238.818L66.4486 286.641Z"
					fill="#4C4C4C"
				/>
			</svg>
		</div>

		<Container
			class="relative z-10 flex flex-col gap-[60px] px-5 pb-8 pt-20 sm:px-8 sm:pb-11 md:pb-11 md:pt-20 lg:px-12 lg:py-[100px]"
		>
			<!-- Desktop: Grid layout (hidden on mobile/tablet) -->
			<div class="hidden gap-6 lg:flex">
				<template v-if="isLoading">
					<div
						v-for="index in 3"
						:key="`skeleton-${index}`"
						class="flex flex-1 flex-col gap-4"
					>
						<Skeleton :is-active="true" variant="primary" class="h-9 w-3/4" />
						<Skeleton :is-active="true" variant="primary" class="h-px w-full" />
						<Skeleton
							:is-active="true"
							variant="primary"
							class="h-[54px] w-full"
						/>
					</div>
				</template>
				<template v-else>
					<div
						v-for="item in items"
						:key="item.id"
						class="flex flex-1 flex-col gap-4 overflow-hidden"
					>
						<h3
							class="font-matter-sq text-2xl font-normal leading-[150%] tracking-[-0.48px] text-white xl:text-[28px]"
						>
							{{ item.title }}
						</h3>
						<div
							class="h-px w-full bg-[#4c4c4c]"
							aria-hidden="true"
							data-testid="decorative-divider"
						/>
						<p
							class="font-sans text-lg font-normal leading-[150%] text-[#f2f2f2] xl:text-xl"
						>
							{{ item.description }}
						</p>
					</div>
				</template>
			</div>

			<!-- Mobile/Tablet: Carousel layout -->
			<div class="relative lg:hidden">
				<Carousel
					ref="carouselContainerRef"
					class="w-full"
					:plugins="[WheelGesturesPlugin()]"
					:opts="{ align: 'start' }"
				>
					<CarouselContent class="gap-6" main-class="!overflow-visible">
						<template v-if="isLoading">
							<CarouselItem
								v-for="index in 3"
								:key="`skeleton-mobile-${index}`"
								class="basis-[90%] sm:basis-[45%] xl:basis-[32.3%]"
							>
								<div class="flex flex-col gap-4">
									<Skeleton
										:is-active="true"
										variant="primary"
										class="h-9 w-3/4"
									/>
									<Skeleton
										:is-active="true"
										variant="primary"
										class="h-px w-full"
									/>
									<Skeleton
										:is-active="true"
										variant="primary"
										class="h-[54px] w-full"
									/>
								</div>
							</CarouselItem>
						</template>
						<template v-else>
							<CarouselItem
								v-for="item in items"
								:key="item.id"
								class="basis-[90%] sm:basis-[45%] xl:basis-[32.3%]"
							>
								<div class="flex flex-col gap-4 overflow-hidden">
									<h3
										class="font-matter-sq text-2xl font-normal leading-normal tracking-[-0.48px] text-white"
									>
										{{ item.title }}
									</h3>
									<div
										class="h-px w-full bg-[#4c4c4c]"
										aria-hidden="true"
										data-testid="decorative-divider"
									/>
									<p
										class="font-sans text-lg font-normal leading-normal text-[#f2f2f2]"
									>
										{{ item.description }}
									</p>
								</div>
							</CarouselItem>
						</template>
					</CarouselContent>
				</Carousel>

				<!-- Pagination controls (mobile/tablet only) -->
				<div class="mt-[60px] flex items-center justify-between gap-2">
					<div
						ref="carouselItemSelectorContainerRef"
						class="flex flex-1 justify-end gap-2"
					>
						<CarouselItemSelector
							v-for="(_, index) in scrollableSlidesNumber"
							:key="`selector-${index}`"
							:active="currentCarouselItemIndex === index"
							:delay="PAGINATION_DELAY_MS"
							:dynamic-width="carouselDynamicItemWidth"
							variant="white"
							:aria-current="
								currentCarouselItemIndex === index ? 'true' : undefined
							"
							@click="scrollTo(index)"
						/>
					</div>
					<div class="flex gap-0">
						<CarouselGenericAction
							variant="default"
							:aria-label="'Slide anterior'"
							@click="scrollTo(currentCarouselItemIndex - 1)"
						>
							<IconMaterialSymbolsLightChevronLeft width="28px" height="28px" />
						</CarouselGenericAction>
						<CarouselGenericAction
							variant="default"
							:aria-label="'Próximo slide'"
							@click="scrollTo(currentCarouselItemIndex + 1)"
						>
							<IconMaterialSymbolsLightChevronLeft
								width="28px"
								height="28px"
								class="rotate-180"
							/>
						</CarouselGenericAction>
					</div>
				</div>
			</div>
		</Container>
	</section>
</template>
